import React, { useState } from 'react';
import styled from 'styled-components/macro';
import cx from 'classnames';
import Toggle from 'react-toggle';
import { FormattedDate, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlayCircle } from '@fortawesome/pro-light-svg-icons';
import { useDispatch } from 'react-redux';
import { TABLET_LANDSCAPE } from '@tovia/man-ui';
import moment from 'moment';
import camelCase from 'lodash/camelCase';
import { ImageDetailsPanel } from '../../components/panels/ImageDetailsPanel';
import { formatDateDefaultOptions, formatDescription, modelLinks, secondsToRuntime } from '../../utils/converters';
import { toggleLights as toggleLightsFn } from '../../redux/modules/movie';
import { useSelector } from 'src/client/redux';
import { NetworkLink } from '../../components/buttons/NetworkLink';
import { MOBILE_LANDSCAPE } from '../../constants/mediaQueries';
import { Movie } from 'src/@types/Movie';
import { generateGalleryAlt } from 'src/client/utils/generateGalleryAlt';
import { useGetGalleryCoverUrl } from 'src/client/helpers/urls/useGetGalleryCoverUrl';
import { useShowSfwSite } from 'src/client/helpers/useFeatureFlags';
import { SFW_DATE_THRESHOLD } from 'src/shared/constants/userTypes';

const truncate = (p) => p.split(/\s+/).slice(0, 40).join(' ');

type Props = {
  isTrailer: boolean;
  movie: Movie;
  className?: string;
};

export const MovieDetailsPanel = (props: Props) => {
  const dispatch = useDispatch();
  const toggleLights = () => dispatch(toggleLightsFn());

  const contentCdnUrl = useSelector((state) => state.app.contentCdnUrl);
  const defaultCdnUrl = useSelector((state) => state.app.defaultCdnUrl);
  const config = useSelector((state) => state.app.config);
  const isLightsOn = useSelector((state) => state.movie.isLightsOn);
  const { hasWideMovieCovers } = useSelector((state) => state.app.config);
  const showSfwSite = useShowSfwSite();

  const intl = useIntl();
  const getGalleryCover = useGetGalleryCoverUrl();

  const [isTruncated, setIsTruncated] = useState(true);

  const { movie, isTrailer } = props;
  const { publishedAt, name, files, description, crew, models } = movie;

  const cdn = movie.isStaffSelection ? defaultCdnUrl : contentCdnUrl;
  const wideThumbnail = !!hasWideMovieCovers;
  const galleryCover = getGalleryCover(cdn, movie, {
    clean: config.cleanGalleryCovers,
    thumbnail: wideThumbnail,
    wide: wideThumbnail,
  });
  const hasTrailer = !!files.teasers.length;
  const runtime = secondsToRuntime(movie.media.runtime);
  const isNetworkWideGallery = movie.isStaffSelection || movie.isIntimateSelection;
  const cast = modelLinks(movie.models, isNetworkWideGallery);
  const publishedMoment = moment(publishedAt);
  const releaseYear = publishedMoment.utc().format('YYYY');

  const showDescriptionPanel = Boolean(
    description &&
      description !== '' &&
      // All movie descriptions published after Feb 1, 2025 will be SFW
      (!showSfwSite || (showSfwSite && publishedMoment.isAfter(SFW_DATE_THRESHOLD))),
  );

  const director =
    // removing mai from using director credit since it's almost always the model self shooting these
    movie.photographers[0] && !movie.isIntimateSelection ? (
      <NetworkLink
        isNetworkWideGallery={isNetworkWideGallery}
        key={movie.photographers[0].UUID}
        to={movie.photographers[0].path}
      >
        {movie.photographers[0].name}
      </NetworkLink>
    ) : null;

  const renderLightsLowerBtn = () => (
    <LightsButton className={cx('btn', 'btn-default', { dimmed: !isLightsOn })} onClick={toggleLights}>
      {!isLightsOn
        ? intl.formatMessage({ id: 'movie.buttons.raiseLights', defaultMessage: 'Raise Lights:' })
        : intl.formatMessage({ id: 'movie.buttons.lowerLights', defaultMessage: 'Lower Lights:' })}
      <LightsToggle checked={!isLightsOn} icons={false} onChange={toggleLights} />
    </LightsButton>
  );

  const credits = [
    {
      label: intl.formatMessage({ id: 'movieDetailsPanel.cast', defaultMessage: 'Cast' }),
      value: cast,
    },
    {
      label: intl.formatMessage({ id: 'movieDetailsPanel.released', defaultMessage: 'Released' }),
      value: <FormattedDate {...formatDateDefaultOptions} month="short" value={publishedAt} />,
    },
    {
      label: intl.formatMessage({ id: 'movieDetailsPanel.runtime', defaultMessage: 'Runtime' }),
      value: runtime,
    },
  ];

  // Director goes in after the cast.
  if (director) {
    credits.splice(1, 0, {
      label: intl.formatMessage({ id: 'movieDetailsPanel.director', defaultMessage: 'Director' }),
      value: director,
    });
  }

  if (crew) {
    crew.forEach((item) => {
      credits.push({
        label: intl.formatMessage({ id: `movieDetailsPanel.${camelCase(item.role)}`, defaultMessage: item.role }),
        value: item.names.join(', '),
      });
    });
  }

  return (
    <MovieImageDetailsPanel
      isBlurred={galleryCover.blurImage}
      imgSrc={galleryCover.url}
      imgAlt={generateGalleryAlt(name, models)}
      className={props.className}
    >
      <MovieHeadline>
        {name} <small>({releaseYear})</small>
      </MovieHeadline>

      {isTrailer && (
        <TrailerButtons>
          {hasTrailer && (
            <WatchButton type="button" className="btn btn-primary" onClick={() => window.jwplayer().play()}>
              <FontAwesomeIcon icon={faPlayCircle} size="2x" />
              Watch Trailer
            </WatchButton>
          )}
          {renderLightsLowerBtn()}
        </TrailerButtons>
      )}

      {showDescriptionPanel && (
        <Description>
          {isTruncated ? (
            <>
              {truncate(description) + '... '}
              <a className="clickable" onClick={() => setIsTruncated(false)}>
                {intl.formatMessage({ id: 'movie.buttons.readMore', defaultMessage: 'Read More' })}
              </a>
            </>
          ) : (
            <>
              {formatDescription(description)}
              <a className="clickable" onClick={() => setIsTruncated(true)}>
                {intl.formatMessage({ id: 'movie.buttons.hide', defaultMessage: 'Hide' })}
              </a>
            </>
          )}
        </Description>
      )}

      <Description as="ul" className="list-unstyled" data-testid="movie-data">
        {credits
          .filter((c) => !!c)
          .map((credit) => (
            <li key={credit.label}>
              <CreditsKey>{credit.label}:</CreditsKey>
              <span>{credit.value}</span>
            </li>
          ))}
      </Description>
    </MovieImageDetailsPanel>
  );
};

const MovieImageDetailsPanel = styled(ImageDetailsPanel)`
  img {
    @media ${MOBILE_LANDSCAPE} {
      max-width: 227px;
    }
  }
`;

const TrailerButtons = styled.div`
  margin: 10px 0;
  display: none;

  /* Investigate: Why Only Visible on Desktop (?) */
  @media ${TABLET_LANDSCAPE} {
    display: block;
  }
`;

const WatchButton = styled.button`
  font-weight: 600;
  margin-bottom: 10px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

const LightsButton = styled.button`
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

const LightsToggle = styled(Toggle)`
  &.react-toggle--checked {
    .react-toggle-track {
      background: rgb(156, 156, 156);
    }

    .react-toggle-thumb {
      border-color: rgb(156, 156, 156);
    }

    &:hover:not(.react-toggle--disabled) .react-toggle-track {
      background: rgb(156, 156, 156);
    }
  }

  &.react-toggle:active:not(.react-toggle--disabled) .react-toggle-thumb,
  &.react-toggle--focus .react-toggle-thumb {
    box-shadow: 0 0 2px #fff;
  }
`;

const Description = styled.div`
  font-size: 0.95rem;

  &:not(:last-child) {
    margin-bottom: 10px;
  }

  a {
    text-decoration: underline;
  }
`;

const CreditsKey = styled.span`
  margin-right: 4px;
  color: ${(props) => props.theme.primary7};
`;

const MovieHeadline = styled.h1`
  margin-top: 0;
  font-size: 1.65rem;
  margin-bottom: 10px;
`;
